import React from "react";
import { AlertTriangle } from "lucide-react";

// "Add your numbers" — the AI resume optimizer inserts metric placeholders like
// [X%], [X days], [X vendors] (flagged CONSIDER) where a number would strengthen a
// bullet but wasn't in the original resume, and is told never to invent the figure.
// This module lets the candidate supply those numbers during review. Reusable so the
// same flow can be dropped into the Resume Analyzer later.
//
//  detectPlaceholders(resume)      -> [{ id, path, token, inner, context }]
//  applyPlaceholders(resume, vals) -> new resume with tokens filled, blanks cleaned
//  <AddYourNumbers ...>            -> the guided checklist UI

const TOKEN_RE = /\[[^\]]*X[^\]]*\]/g;

// Deep-walk the structured resume, finding every placeholder token inside any string.
export function detectPlaceholders(resume) {
  const found = [];
  const walk = (node, path) => {
    if (typeof node === "string") {
      let m;
      const re = new RegExp(TOKEN_RE.source, "g");
      while ((m = re.exec(node)) !== null) {
        found.push({
          id: `${path.join(".")}@${m.index}`,
          path,
          index: m.index,
          token: m[0],
          inner: m[0].slice(1, -1),   // e.g. "X%", "X days", "X"
          context: node,               // the full bullet/string, for display
        });
      }
    } else if (Array.isArray(node)) {
      node.forEach((v, i) => walk(v, [...path, i]));
    } else if (node && typeof node === "object") {
      Object.keys(node).forEach((k) => walk(node[k], [...path, k]));
    }
  };
  walk(resume || {}, []);
  return found;
}

const cleanSentence = (s) =>
  s
    // remove a blank placeholder plus a leading connective and/or throwaway trailing unit
    // noun; replace with a single space so surrounding words don't get jammed together.
    .replace(
      /\s*\b(?:by|to|across|of|for|with|reaching|over|up to|within|in)?\s*\[[^\]]*X[^\]]*\]\s*(?:vendors|clients|days|hours|weeks|months|years|percent)?/gi,
      " "
    )
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,;:])/g, "$1")
    .replace(/\(\s*\)/g, "")
    .trim();

// Fill provided values into the resume; any placeholder left blank is removed and its
// bullet is tidied into a clean qualitative statement (never ships a raw [X%]).
export function applyPlaceholders(resume, values = {}) {
  const clone = JSON.parse(JSON.stringify(resume || {}));
  const items = detectPlaceholders(clone);
  const byPath = {};
  items.forEach((p) => {
    const key = p.path.join(" ");
    (byPath[key] = byPath[key] || []).push(p);
  });
  Object.entries(byPath).forEach(([key, list]) => {
    const path = key.split(" ").map((seg) => (/^\d+$/.test(seg) ? Number(seg) : seg));
    let ref = clone;
    for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
    const lastKey = path[path.length - 1];
    let str = ref[lastKey];
    // Phase 1: substitute filled tokens (in document order).
    let occ = 0;
    str = str.replace(new RegExp(TOKEN_RE.source, "g"), (token) => {
      const p = list[occ++];
      const val = (values[p?.id] ?? "").toString().trim();
      return val ? token.slice(1, -1).replace(/X/, val) : token;
    });
    // Phase 2: any remaining (blank) tokens get cleaned away with their connective.
    ref[lastKey] = cleanSentence(str);
  });
  return clone;
}

function HighlightPlaceholders({ text }) {
  const parts = text.split(/(\[[^\]]*X[^\]]*\])/g);
  return (
    <>
      {parts.map((s, i) =>
        /^\[[^\]]*X[^\]]*\]$/.test(s) ? (
          <span key={i} className="px-1 rounded bg-[#F59E0B]/25 text-[#B45309] font-semibold">
            {s.slice(1, -1).replace(/X/, "__")}
          </span>
        ) : (
          <span key={i}>{s}</span>
        )
      )}
    </>
  );
}

export default function AddYourNumbers({ placeholders, values, onChange }) {
  if (!placeholders || placeholders.length === 0) return null;

  // Group by the bullet the placeholders live in, so a bullet with two blanks shows once.
  const groups = [];
  const byKey = {};
  placeholders.forEach((p) => {
    const key = p.path.join(".");
    if (!byKey[key]) { byKey[key] = { context: p.context, items: [] }; groups.push(byKey[key]); }
    byKey[key].items.push(p);
  });

  const filled = placeholders.filter((p) => (values[p.id] ?? "").toString().trim()).length;
  const remaining = placeholders.length - filled;

  return (
    <div className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-4">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-[#B45309] flex-shrink-0" />
        <span className="font-semibold text-sm text-[#0F172A]">Add your numbers</span>
        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/20 text-[#B45309]">
          {remaining > 0 ? `${remaining} to fill` : "all set"}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Your tailored resume has a few blanks where a metric makes the bullet stronger. Fill what you can —
        leave any blank and we'll drop it (we never invent numbers).
      </p>

      <div className="space-y-2.5">
        {groups.map((g, gi) => (
          <div key={gi} className="rounded-lg border border-border bg-white p-3">
            <div className="text-xs text-[#0F172A] leading-relaxed mb-2.5">
              <HighlightPlaceholders text={g.context} />
            </div>
            <div className="flex flex-wrap gap-2">
              {g.items.map((p) => {
                const val = (values[p.id] ?? "").toString();
                const unit = p.inner.replace(/X/, "").trim();   // "%", "days", ""
                return (
                  <div key={p.id} className="inline-flex items-center gap-1.5">
                    <input
                      value={val}
                      onChange={(e) => onChange(p.id, e.target.value)}
                      inputMode="decimal"
                      placeholder={p.inner.replace(/X/, "__")}
                      className="w-24 rounded-lg border border-border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0025D8]/30 focus:border-[#0025D8]"
                    />
                    {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
